import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import {
  useApi,
  identityApiRef,
  configApiRef,
} from '@backstage/core-plugin-api';
import { Progress } from '@backstage/core-components';
import { EpDevPNotFoundErrorPage } from '@solace-labs/backstage-plugin-ep-devp';

// Define the type for user role (if not already defined)
type UserRoleResponse = {
  userID: string;
  roleID: string;
  roleName: string;
};

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles: string[];
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  allowedRoles,
}) => {
  const config = useApi(configApiRef);
  const baseUrl = config.getString('backend.baseUrl');
  const identityApi = useApi(identityApiRef);
  const [userRole, setUserRole] = useState<UserRoleResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchUserRole = async () => {
      try {
        setIsLoading(true);
        const profile = await identityApi.getProfileInfo();
        const { token } = await identityApi.getCredentials();
        const response: Response = await fetch(`${baseUrl}/api/users/Role`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ email: profile.email }),
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch user role: ${response.statusText}`);
        }

        const userRoleResponse: UserRoleResponse = await response.json();
        setUserRole(userRoleResponse);
      } catch (error) {
        console.error(error);
        // Optionally set an error state here
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserRole();
  }, [identityApi]);

  // Show loading state while checking role
  if (isLoading) {
    return <Progress />;
  }

  // Check if user's role is in the allowed roles
  const isAuthorized = userRole && allowedRoles.includes(userRole.roleName);

  // If not authorized, redirect to unauthorized page
  if (!isAuthorized) {
    return (
      <EpDevPNotFoundErrorPage status="404" statusMessage="PAGE NOT FOUND" />
    );
  }

  // If authorized, render the children
  return (
    <>
      {React.Children.map(children, child => {
        if (React.isValidElement(child)) {
          return React.cloneElement(child, { userRole } as any);
        }
        return child;
      })}
    </>
  );
};
